import React, { useEffect, useRef, useState } from "react";

import { COLLAPSED_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH } from "./constants";
import LeftSidebarCollapseButton from "./LeftSidebarCollapseButton";
import LeftSidebarResizeHandle from "./LeftSidebarResizeHandle";
import { getInitialLayoutState, usePageLayoutState } from "./usePageLayoutState";

import { LeftSidebarContainer, LeftSidebarContent, LeftSidebarWrapper } from "./LeftSidebar.styles";

interface LeftSidebarProps {
  testId?: string;
  children: React.ReactNode;
}

/**
 * Left sidebar component for navigation.
 * Width is read from localStorage (default 240px).
 * Supports collapse/expand via button.
 * Sets the --leftSidebarWidth CSS custom property.
 */
const LeftSidebar: React.FC<LeftSidebarProps> = ({ testId, children }) => {
  const [width, setWidth] = useState(() => getInitialLayoutState().gridState.leftSidebarWidth);
  const { isLeftSidebarCollapsed, setIsLeftSidebarCollapsed, setLeftSidebarWidth } = usePageLayoutState();
  const [isHoverExpanded, setIsHoverExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const effectiveWidth = isLeftSidebarCollapsed && !isHoverExpanded ? COLLAPSED_SIDEBAR_WIDTH : width;

  useEffect(() => {
    // Set CSS custom property for left sidebar width
    document.documentElement.style.setProperty("--leftSidebarWidth", `${effectiveWidth}px`);

    return () => {
      // Clean up on unmount
      document.documentElement.style.removeProperty("--leftSidebarWidth");
    };
  }, [effectiveWidth]);

  const handleToggle = () => {
    setIsLeftSidebarCollapsed(!isLeftSidebarCollapsed);
  };

  const handleMouseEnter = () => {
    if (isLeftSidebarCollapsed) {
      setIsHoverExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHoverExpanded(false);
  };

  const handleResizeEnd = (newWidth: number) => {
    // If dragged below minimum width, collapse instead
    if (newWidth < MIN_SIDEBAR_WIDTH) {
      setIsLeftSidebarCollapsed(true);
    } else {
      // Update local state and save to localStorage when drag completes
      setWidth(newWidth);
      setLeftSidebarWidth(newWidth);
    }
  };

  return (
    <LeftSidebarWrapper data-testid={testId} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <LeftSidebarContainer ref={containerRef} $width={effectiveWidth} $isCollapsed={isLeftSidebarCollapsed}>
        <LeftSidebarContent $isCollapsed={isLeftSidebarCollapsed && !isHoverExpanded} $contentWidth={width}>
          {children}
        </LeftSidebarContent>
        <LeftSidebarResizeHandle
          isCollapsed={isLeftSidebarCollapsed || isHoverExpanded}
          containerRef={containerRef}
          onResizeEnd={handleResizeEnd}
          onCollapse={() => setIsLeftSidebarCollapsed(true)}
        />
      </LeftSidebarContainer>
      <LeftSidebarCollapseButton isCollapsed={isLeftSidebarCollapsed} onToggle={handleToggle} />
    </LeftSidebarWrapper>
  );
};

export default LeftSidebar;
